import { TYPO } from "styles/Typo";
import DiagnosisLayout from "./Layout";
import { useIndustryMMTData } from "utils/clientAPIs/detailDiagnosis";
import { useParams } from "next/navigation";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import SkeletonBlock from "components/skeleton";
import moment from "moment-timezone";
import { TrackedLink } from "lib/analytics/react/link";

function DetailDiagnosisMomentum({
  score,
  base_dt,
  detailData,
}: {
  score?: number;
  base_dt?: string;
  detailData: any;
}) {
  const router = useRouter();
  const { t } = useTranslation("detail");
  const { ticker } = useParams();
  const fixedWidth = 120;
  const Headers = [
    {
      title: t("industryCol"),
      key: "industry",
    },
    {
      title: t("peerCompanies"),
      key: "companyCount",
    },
    {
      title: t("upStrength"),
      key: "upStrength",
    },
  ];
  const [mmtData, isLoading] = useIndustryMMTData(
    ticker as string,
    detailData?.ITEM_CD,
    detailData?.ITEM_CD !== undefined,
    true
  );
  const Text = ({
    text,
    index,
    className,
  }: {
    text: string;
    index: number;
    className?: string;
  }) => {
    return (
      <p
        key={index}
        className={`${TYPO.TYPO7_1_RG} text-gray80 flex-1 grow ${className}`}
        style={{
          width: index === 0 ? fixedWidth : "auto",
          textAlign:
            index === Headers.length - 1
              ? "right"
              : index === 0
              ? "left"
              : "center",
        }}
      >
        {text}
      </p>
    );
  };

  const loading = (!score && String(score) !== "0") || isLoading || !detailData;
  const lastUpdateDate =
    router.locale === "ko"
      ? moment(base_dt).format("M월 D일")
      : moment(base_dt).format("MMM D");

  if (loading) {
    return (
      <div className="px-6 pt-[30px] pb-1 gap-1 flex flex-col">
        <SkeletonBlock width={"100%"} height={289} radius={10} />
      </div>
    );
  }

  return (
    <div className="px-6 pt-[30px] pb-1 gap-1 flex flex-col">
      <DiagnosisLayout>
        <DiagnosisLayout.Header score={score}>
          <DiagnosisLayout.Header.Title>
            {t("industryMomentum")}
          </DiagnosisLayout.Header.Title>
          <DiagnosisLayout.Header.SubTitle>
            {score && score >= 50
              ? t("industryPeersRising")
              : t("industryPeersWeak")}
          </DiagnosisLayout.Header.SubTitle>
        </DiagnosisLayout.Header>
        <DiagnosisLayout.ContentWrapper height={200}>
          <DiagnosisLayout.Content>
            {Headers.map((header, index) => (
              <Text
                text={header.title}
                index={index}
                key={index}
                className="!text-gray50 whitespace-nowrap"
              />
            ))}
          </DiagnosisLayout.Content>

          {mmtData?.industry?.map((item: any, index: number) => (
            <DiagnosisLayout.Content key={index}>
              <Text text={item?.TU_NM} index={0} />
              <Text
                text={item?.stockCount}
                index={1}
                className="!text-gray50"
              />
              <div className="flex grow flex-1 justify-end items-center">
                <p
                  className={`${TYPO.TYPO7_1_MD} bg-gray5 rounded-[4px] px-2 w-fit text-gray80 `}
                  style={{
                    textAlign: "right",
                  }}
                >
                  {item?.MOM_PER}
                </p>
              </div>
            </DiagnosisLayout.Content>
          ))}
        </DiagnosisLayout.ContentWrapper>
        <TrackedLink
          href={`/detail/${ticker}/mmt`}
          itemspec={{
            kind: "section_nav",
            section_id: "stock_attractiveness_mmt",
            nav_key: "stock_navbtn_mmt_detail",
          }}
          asTrace={true}
        >
          <DiagnosisLayout.ViewMore
            onClick={() => {
              router.push(`/detail/${ticker}/mmt`);
            }}
            type="mmt"
          />
        </TrackedLink>
      </DiagnosisLayout>
      <div className="flex justify-end">
        <p className={`${TYPO.TYPO8_RG} text-gray40`}>
          {t("lastUpdated", { date: lastUpdateDate })}
        </p>
      </div>
    </div>
  );
}

export default DetailDiagnosisMomentum;
